import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import {
    Calendar, AlertOctagon, CheckCircle2, AlertTriangle,
    Zap, Activity, User, Clock, ArrowUpRight,
    Shield, Target, Award
} from 'lucide-react';
import StatCard from './ui/StatCard';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "./ui/card";
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { cn } from '../lib/utils';

const STRATEGY_CONFIG = {
    ENTERPRISE: { label: 'Enterprise (High Touch)', color: 'bg-indigo-500' },
    MID_MARKET: { label: 'Mid-Market (Balanced)', color: 'bg-blue-500' },
    SMB: { label: 'SMB (Digital Touch)', color: 'bg-slate-500' },
    DEFAULT: { label: 'Global Default', color: 'bg-muted-foreground/30' }
};

export default function CustomerSuccessDashboard({ onDrilldown, filters = {} }) {
    const [overview, setOverview] = useState(null);
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period] = useState('30d');
    const filtersString = JSON.stringify(filters);

    const loadDashboard = useCallback(async () => {
        try {
            const filterQuery = new URLSearchParams({
                period,
                ...filters
            }).toString();

            const [overviewRes, ownersRes] = await Promise.all([
                api.get(`/api/success/reporting/overview?${filterQuery}`),
                api.get('/api/success/reporting/owners')
            ]);
            setOverview(overviewRes.data);
            setOwners(ownersRes.data);
            setError(null);
        } catch (err) {
            setError('Failed to load operations dashboard.');
        } finally {
            setLoading(false);
        }
    }, [period, filters]);

    useEffect(() => {
        loadDashboard();
    }, [period, filtersString, loadDashboard]);

    if (loading) {
        return (
            <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-3xl" />)}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <Skeleton className="h-[400px] rounded-3xl lg:col-span-1" />
                    <Skeleton className="h-[400px] rounded-3xl lg:col-span-2" />
                </div>
            </div>
        );
    }
    if (error) return (
        <div className="p-16 text-center bg-destructive/5 rounded-3xl border border-destructive/20 flex flex-col items-center">
            <AlertOctagon className="w-12 h-12 text-destructive mb-4" />
            <h3 className="text-lg font-black uppercase tracking-widest text-destructive">Dashboard Failure</h3>
            <p className="text-sm font-bold text-muted-foreground mt-2 max-w-sm">{error}</p>
            <button onClick={loadDashboard} className="mt-6 px-6 py-2 bg-background border border-border rounded-xl text-xs font-black uppercase tracking-widest hover:bg-muted transition-all">Retry Analysis</button>
        </div>
    );

    const totalAccounts = Object.values(overview.riskDistribution).reduce((a, b) => a + b, 0);
    const totalOnboarding = Object.values(overview.onboardingDistribution).reduce((a, b) => a + b, 0);
    return (
        <div className="space-y-6">

            {/* Top Stat Cards - Premium Upgrade */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="High-Risk Concentration"
                    value={overview.riskDistribution.HIGH + overview.riskDistribution.CRITICAL}
                    icon={AlertTriangle}
                    description="CRITICAL & HIGH cohort drilldown"
                    variant="danger"
                    className="h-full"
                    onClick={() => onDrilldown({ risk: 'HIGH', onboarding: 'ALL', lifecycle: 'ALL' })}
                />
                <StatCard 
                    title="Renewal Momentum"
                    value={overview.renewals.due30Days}
                    icon={Calendar}
                    description={overview.renewals.overdue > 0 ? `${overview.renewals.overdue} currently past due` : 'Zero renewals currently overdue'}
                    variant={overview.renewals.overdue > 0 ? "danger" : "primary"}
                    className="h-full"
                    onClick={() => onDrilldown({ lifecycle: 'RENEWAL' })}
                />
                <StatCard 
                    title="Implementation Stalls"
                    value={overview.onboardingDistribution.BLOCKED}
                    icon={Zap}
                    description="Action required for blocked accounts"
                    variant={overview.onboardingDistribution.BLOCKED > 0 ? "warning" : "success"}
                    className="h-full"
                    onClick={() => onDrilldown({ onboarding: 'BLOCKED' })}
                />
                <StatCard 
                    title="Team Execution Pulse"
                    value={overview.trends.tasksClosed.current}
                    icon={CheckCircle2}
                    description={`Resolved strategy tasks this ${period === '30d' ? 'month' : 'window'}`}
                    variant="success"
                    className="h-full"
                    onClick={() => window.location.href = '/tasks?status=PENDING'}
                />
            </div>

            {/* Task Pulse Overview (Current Window) and Distributions Section */}
            <div className="grid grid-cols-3 gap-6">

            {/* Operational Task Pulse Upgrade */}
            <div className="col-span-3">
                <Card className="rounded-3xl border-border shadow-md overflow-hidden group hover:border-primary/20 transition-all">
                    <CardHeader className="bg-muted/30 border-b border-border/50 flex flex-row items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-xl border border-primary/20 text-primary">
                                <Activity className="w-4 h-4" />
                            </div>
                            <div>
                                <CardTitle className="text-sm font-black uppercase tracking-widest text-foreground">Operational Task Pulse</CardTitle>
                                <CardDescription className="text-[10px] font-bold text-muted-foreground uppercase opacity-60">Success signals generated this period</CardDescription>
                            </div>
                        </div>
                        <div className="flex gap-8">
                            <div className="text-right">
                                <div className="text-2xl font-black text-primary leading-none">{overview.taskPulse.generatedCurrent}</div>
                                <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-1 opacity-60">Generated</div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-black text-foreground leading-none">{overview.taskPulse.openNow}</div>
                                <div className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-1 opacity-60">Open Now</div>
                            </div>
                        </div>
                    </CardHeader>
                </Card>
            </div>

            {/* Distributions Upgrade */}
            <div className="col-span-1 space-y-8 mt-2">
                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <Shield className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Health Distribution</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(overview.riskDistribution).map(([tier, count]) => (
                            <div 
                                key={tier} 
                                className="space-y-1.5 group/bar cursor-pointer"
                                onClick={() => onDrilldown({ risk: tier, onboarding: 'ALL', lifecycle: 'ALL' })}
                            >
                                <div className="flex justify-between text-[11px] font-black uppercase">
                                    <span className="text-muted-foreground opacity-60 group-hover/bar:text-primary transition-colors">{tier}</span>
                                    <span className="text-foreground">{count}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2 border border-border/50 overflow-hidden group-hover/bar:border-primary/20 transition-all">
                                    <div
                                        className={cn(
                                            "h-2 rounded-full transition-all duration-1000",
                                            tier === 'LOW' ? 'bg-emerald-500' : tier === 'MEDIUM' ? 'bg-amber-500' : tier === 'HIGH' ? 'bg-orange-500' : 'bg-destructive'
                                        )}
                                        style={{ width: `${totalAccounts > 0 ? (count / totalAccounts) * 100 : 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                        {totalAccounts === 0 && <p className="text-[10px] font-black uppercase text-muted-foreground/40 text-center py-4 tracking-widest">No Intelligence Data Detected</p>}
                    </CardContent>
                </Card>

                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <Target className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Onboarding Status Index</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(overview.onboardingDistribution).map(([state, count]) => (
                            <div 
                                key={state} 
                                className="space-y-1.5 group/bar cursor-pointer"
                                onClick={() => onDrilldown({ onboarding: state, risk: 'ALL', lifecycle: 'ALL' })}
                            >
                                <div className="flex justify-between text-[11px] font-black uppercase">
                                    <span className="text-muted-foreground opacity-60 group-hover/bar:text-primary transition-colors">{state.replace('_', ' ')}</span>
                                    <span className="text-foreground">{count}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2 border border-border/50 overflow-hidden group-hover/bar:border-primary/20 transition-all">
                                    <div
                                        className={cn(
                                            "h-2 rounded-full transition-all duration-1000",
                                            state === 'COMPLETE' ? 'bg-emerald-500' : state === 'IN_PROGRESS' ? 'bg-primary' : state === 'BLOCKED' ? 'bg-destructive' : 'bg-muted-foreground/30'
                                        )}
                                        style={{ width: `${totalOnboarding > 0 ? (count / totalOnboarding) * 100 : 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                        {totalOnboarding === 0 && <p className="text-[10px] font-black uppercase text-muted-foreground/40 text-center py-4 tracking-widest">Scanning Implementation Status...</p>}
                    </CardContent>
                </Card>

                {/* New Intelligence: Risk Drivers */}
                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <AlertOctagon className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Primary Risk Drivers</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(overview.reasonDistribution || {}).length === 0 ? (
                            <div className="py-4 text-center text-[10px] font-black text-muted-foreground uppercase opacity-40">All accounts healthy</div>
                        ) : Object.entries(overview.reasonDistribution).sort((a,b) => b[1] - a[1]).map(([reason, count]) => (
                            <div key={reason} className="space-y-1.5">
                                <div className="flex justify-between text-[11px] font-black uppercase">
                                    <span className="text-muted-foreground opacity-60 truncate max-w-[180px]">{reason}</span>
                                    <span className="text-foreground">{count}</span>
                                </div>
                                <div className="w-full bg-muted rounded-full h-1.5 border border-border/50 overflow-hidden">
                                    <div
                                        className="h-1.5 bg-destructive/60 rounded-full"
                                        style={{ width: `${totalAccounts > 0 ? (count / totalAccounts) * 100 : 0}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                {/* New Intelligence: Renewal Horizons */}
                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Renewal Horizons</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {[
                            { label: 'Critical (<7d)', key: 'CRITICAL', color: 'bg-destructive' },
                            { label: 'Urgent (<30d)', key: 'URGENT', color: 'bg-amber-500' },
                            { label: 'Upcoming (<90d)', key: 'UPCOMING', color: 'bg-primary' },
                            { label: 'Stable (>90d)', key: 'STABLE', color: 'bg-emerald-500' }
                        ].map(tier => {
                            const count = overview.renewals.urgency?.[tier.key] || 0;
                            return (
                                <div key={tier.key} className="space-y-1.5">
                                    <div className="flex justify-between text-[11px] font-black uppercase">
                                        <span className="text-muted-foreground opacity-60">{tier.label}</span>
                                        <span className="text-foreground">{count}</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-1.5 border border-border/50 overflow-hidden">
                                        <div
                                            className={cn("h-1.5 rounded-full transition-all duration-1000", tier.color)}
                                            style={{ width: `${totalAccounts > 0 ? (count / totalAccounts) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                {/* Strategy Segmentation - New in Sprint 8 */}
                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all bg-gradient-to-br from-background to-blue-500/[0.02]">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <Target className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Portfolio Strategy Profiles</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {Object.entries(STRATEGY_CONFIG).map(([key, cfg]) => {
                            const count = overview.segmentDistribution?.[key] || 0;
                            const total = Object.values(overview.segmentDistribution || {}).reduce((a, b) => a + b, 0);
                            if (total === 0 && key !== 'DEFAULT') return null;
                            return (
                                <div key={key} className="space-y-1.5">
                                    <div className="flex justify-between text-[11px] font-black uppercase">
                                        <span className="text-muted-foreground opacity-60">{cfg.label.split(' (')[0]}</span>
                                        <span className="text-foreground">{count}</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-1.5 border border-border/50 overflow-hidden">
                                        <div
                                            className={cn("h-1.5 rounded-full transition-all duration-1000", cfg.color)}
                                            style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                {/* New Intelligence: Strategic Review Cadence - New in Sprint 7 */}
                <Card className="rounded-3xl border-border shadow-sm group hover:border-primary/10 transition-all bg-gradient-to-br from-background to-primary/5">
                    <CardHeader className="pb-4">
                        <div className="flex items-center gap-3">
                            <Award className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">Review Cadence Index</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {[
                            { label: 'Up to Date', key: 'UP_TO_DATE', color: 'bg-emerald-500' },
                            { label: 'Overdue (>90d)', key: 'OVERDUE', color: 'bg-destructive' },
                            { label: 'Pending Initial', key: 'PENDING_INITIAL', color: 'bg-muted-foreground/30' }
                        ].map(status => {
                            const count = overview.reviewStatus?.[status.key] || 0;
                            const total = (overview.reviewStatus?.UP_TO_DATE || 0) + (overview.reviewStatus?.OVERDUE || 0) + (overview.reviewStatus?.PENDING_INITIAL || 0);
                            return (
                                <div key={status.key} className="space-y-1.5">
                                    <div className="flex justify-between text-[11px] font-black uppercase">
                                        <span className="text-muted-foreground opacity-60">{status.label}</span>
                                        <span className="text-foreground">{count}</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-1.5 border border-border/50 overflow-hidden">
                                        <div
                                            className={cn("h-1.5 rounded-full transition-all duration-1000", status.color)}
                                            style={{ width: `${total > 0 ? (count / total) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>
            </div>

            {/* Main Tables */}
            <div className="col-span-2 space-y-6">

            {/* Main Tables Upgrade */}
            <div className="col-span-2 space-y-8 mt-2">

                {/* CSM Workload */}
                <Card className="rounded-3xl border-border shadow-sm overflow-hidden group hover:border-primary/10 transition-all">
                    <CardHeader className="bg-muted/20 border-b border-border/50 py-4">
                        <div className="flex items-center gap-3">
                            <User className="w-4 h-4 text-primary" />
                            <CardTitle className="text-xs font-black uppercase tracking-widest">CSM Optimization Distribution</CardTitle>
                        </div>
                    </CardHeader>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-muted/10 border-b border-border/50">
                                <tr>
                                    <th className="px-6 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest">Operator</th>
                                    <th className="px-3 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest text-center">Avg Health</th>
                                    <th className="px-3 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest text-center" title="Renewals due within 30 days (Critical + Urgent)">
                                        Renewal Pressure
                                    </th>
                                    <th className="px-3 py-4 text-[10px] font-black text-muted-foreground uppercase tracking-widest text-center">At Risk</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50">
                                {owners.length === 0 ? (
                                    <tr><td colSpan="4" className="text-center p-12 text-[10px] font-black text-muted-foreground uppercase opacity-40">No CSM Workload data detected.</td></tr>
                                ) : owners.map((owner, idx) => (
                                    <tr 
                                        key={idx} 
                                        className="hover:bg-muted/30 transition-colors cursor-pointer group"
                                        onClick={() => onDrilldown({ ownerId: owner.owner.userId })}
                                    >
                                        <td className="px-6 py-4 font-black text-foreground group-hover:text-primary transition-colors uppercase tracking-tight text-xs">
                                            <div className="flex items-center gap-2">
                                                {owner.owner.name}
                                                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-all" />
                                            </div>
                                        </td>
                                        <td className="px-3 py-4 text-center font-black">
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-lg text-[10px]",
                                                owner.avgHealthScore < 50 ? "bg-destructive/10 text-destructive" :
                                                owner.avgHealthScore < 80 ? "bg-amber-500/10 text-amber-600" :
                                                "bg-emerald-500/10 text-emerald-600"
                                            )}>
                                                {owner.avgHealthScore}
                                            </span>
                                        </td>
                                        <td className="px-3 py-4 text-center">
                                            <span className={cn(
                                                "font-black text-xs",
                                                owner.renewalPressure > 2 ? "text-destructive" : "text-muted-foreground opacity-60"
                                            )}>
                                                {owner.renewalPressure}
                                            </span>
                                        </td>
                                        <td className="px-3 py-4 text-center font-black text-destructive">{owner.atRiskAccounts}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* Critical Watchlist - Visual Softening for Alarm Fatigue */}
                <Card className="rounded-3xl border-destructive/10 shadow-md overflow-hidden bg-destructive/[0.02] group hover:border-destructive/30 transition-all">
                    <CardHeader className="bg-destructive/[0.03] border-b border-destructive/10 flex flex-row items-center justify-between py-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-destructive/10 rounded-xl border border-destructive/20 text-destructive">
                                <Activity className="w-4 h-4" />
                            </div>
                            <CardTitle className="text-xs font-black uppercase tracking-widest text-destructive/80">Critical Monitoring Watchlist</CardTitle>
                        </div>
                        <Badge variant="outline" className="text-destructive border-destructive/30 font-black text-[10px] tracking-widest bg-background">
                            {overview.criticalAccounts.length} SIGNALED
                        </Badge>
                    </CardHeader>
                    <div className="divide-y divide-destructive/5 max-h-72 overflow-y-auto">
                        {overview.criticalAccounts.length === 0 ? (
                            <div className="p-12 text-center flex flex-col items-center">
                                <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-2 opacity-50" />
                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 text-center leading-relaxed">System Index Clear:<br/>No Critical Exceptions</p>
                            </div>
                        ) : overview.criticalAccounts.map((p, i) => (
                            <div 
                                key={i} 
                                className="p-5 flex flex-col hover:bg-destructive/[0.04] transition-colors cursor-pointer group border-l-2 border-l-transparent hover:border-l-destructive/40" 
                                onClick={() => onDrilldown({ accountId: p._id })}
                            >
                                <div className="flex justify-between items-start mb-1.5">
                                    <div className="flex items-center gap-2">
                                        <span className="font-black text-sm text-foreground group-hover:text-destructive group-hover:underline uppercase tracking-tight transition-colors">
                                            {p.companyId?.name || 'Unknown Entity'}
                                        </span>
                                        <ArrowUpRight className="w-3.5 h-3.5 text-destructive opacity-0 group-hover:opacity-100 transition-all" />
                                    </div>
                                    <span className="text-[9px] font-black bg-background text-destructive border border-destructive/20 px-2 py-0.5 rounded shadow-sm tracking-widest">
                                        SCORE: {p.healthScore}
                                    </span>
                                </div>
                                <p className="text-[11px] font-bold text-muted-foreground/80 leading-relaxed max-w-[95%]">
                                    {p.healthReasons && p.healthReasons.length > 0 ? p.healthReasons[0] : 'Strategic system-level escalation flag.'}
                                </p>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    </div>
</div>
    );
}
